/**
 * AppsFlyer Service
 *
 * Provides AppsFlyer SDK integration for mobile attribution, deep linking,
 * and in-app event tracking. Uses Unified Deep Linking (UDL) for iOS 14.5+ support.
 *
 * Key features:
 * - Install attribution (paid campaigns, organic)
 * - OneLink smart deep links (replaces paire:// scheme for marketing)
 * - In-app event tracking for conversions
 * - Deferred deep linking for new installs
 *
 * @see https://dev.appsflyer.com/hc/docs/capacitor
 */

import { Capacitor } from '@capacitor/core';
import {
  AppsFlyer,
  AFInit,
  AFConstants,
  AFEvent,
} from 'appsflyer-capacitor-plugin';
import { apiClient } from './apiClient';

/**
 * Deep link callback type for UDL (Unified Deep Linking).
 */
export type DeepLinkCallback = (params: {
  deepLinkValue: string;
  deepLinkSub1?: string;
  deepLinkSub2?: string;
  isDeferred: boolean;
}) => void;

/**
 * Conversion data callback for attribution info.
 */
export type ConversionCallback = (data: {
  isFirstLaunch: boolean;
  mediaSource?: string;
  campaign?: string;
  adGroup?: string;
  adSet?: string;
  deepLink?: string;
}) => void;

// Callbacks storage
let deepLinkCallback: DeepLinkCallback | null = null;
let conversionCallback: ConversionCallback | null = null;
let isInitialized = false;

/**
 * AppsFlyer service for mobile attribution and deep linking.
 */
export const appsFlyerService = {
  /**
   * Initialize the AppsFlyer SDK.
   * Should be called once at app startup, after platform is ready.
   *
   * @param onDeepLink - Callback for handling deep links (OneLink)
   * @param onConversion - Callback for handling conversion/attribution data
   */
  async init(
    onDeepLink?: DeepLinkCallback,
    onConversion?: ConversionCallback
  ): Promise<void> {
    // Only initialize on native platforms
    if (Capacitor.getPlatform() === 'web') {
      console.log('[AppsFlyer] Skipping init on web platform');
      return;
    }

    if (isInitialized) {
      console.log('[AppsFlyer] Already initialized');
      return;
    }

    // Store callbacks
    deepLinkCallback = onDeepLink || null;
    conversionCallback = onConversion || null;

    const devKey = import.meta.env.VITE_APPSFLYER_DEV_KEY;
    const appIdIos = import.meta.env.VITE_APPSFLYER_APP_ID_IOS;

    if (!devKey) {
      console.error('[AppsFlyer] Missing VITE_APPSFLYER_DEV_KEY');
      return;
    }

    const config: AFInit = {
      appID: appIdIos || '', // iOS App Store ID (required for iOS, empty for Android)
      devKey,
      isDebug: import.meta.env.DEV,
      waitForATTUserAuthorization: 10, // iOS 14+ ATT prompt timeout in seconds
      registerOnDeepLink: true, // Enable Unified Deep Linking (UDL)
      registerConversionListener: true, // Enable conversion data callbacks
      minTimeBetweenSessions: 5, // Minimum seconds between sessions
    };

    try {
      // Register UDL listener BEFORE initializing SDK
      // This handles OneLink deep links (both direct and deferred)
      AppsFlyer.addListener(AFConstants.UDL_CALLBACK, (response) => {
        console.log('[AppsFlyer] UDL Callback:', JSON.stringify(response));

        if (response.status === 'FOUND' && response.deepLink) {
          const { deep_link_value, deep_link_sub1, deep_link_sub2, is_deferred } =
            response.deepLink;

          if (deepLinkCallback && deep_link_value) {
            deepLinkCallback({
              deepLinkValue: deep_link_value,
              deepLinkSub1: deep_link_sub1,
              deepLinkSub2: deep_link_sub2,
              isDeferred: is_deferred === 'true' || is_deferred === true,
            });
          }
        } else if (response.status === 'NOT_FOUND') {
          console.log('[AppsFlyer] Deep link not found (organic or non-AF link)');
        } else if (response.status === 'ERROR') {
          console.error('[AppsFlyer] UDL Error:', response.error);
        }
      });

      // Register conversion listener for attribution data
      // This provides campaign data for analytics/debugging
      AppsFlyer.addListener(AFConstants.CONVERSION_CALLBACK, (data) => {
        console.log('[AppsFlyer] Conversion Callback:', JSON.stringify(data));

        if (conversionCallback) {
          conversionCallback({
            isFirstLaunch: data.is_first_launch === true || data.is_first_launch === 'true',
            mediaSource: data.media_source,
            campaign: data.campaign,
            adGroup: data.adgroup,
            adSet: data.adset,
            deepLink: data.af_dp,
          });
        }
      });

      // Initialize the SDK
      await AppsFlyer.initSDK(config);

      isInitialized = true;
      console.log('[AppsFlyer] SDK initialized successfully');
    } catch (error) {
      console.error('[AppsFlyer] Initialization failed:', error);
    }
  },

  /**
   * Get the AppsFlyer device ID.
   * This ID is used for S2S event tracking.
   */
  async getAppsFlyerId(): Promise<string | null> {
    if (Capacitor.getPlatform() === 'web') {
      return null;
    }

    try {
      const result = await AppsFlyer.getAppsFlyerUID();
      return result.uid;
    } catch (error) {
      console.error('[AppsFlyer] Failed to get AppsFlyer ID:', error);
      return null;
    }
  },

  /**
   * Set the customer user ID for cross-device matching.
   * Should be called after user authentication.
   *
   * @param userId - The backend user ID
   */
  async setCustomerUserId(userId: string): Promise<void> {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }

    try {
      await AppsFlyer.setCustomerUserId({ cuid: userId });
      console.log('[AppsFlyer] Customer user ID set:', userId);
    } catch (error) {
      console.error('[AppsFlyer] Failed to set customer user ID:', error);
    }
  },

  /**
   * Send the AppsFlyer ID to the backend for S2S event tracking.
   * Should be called after SDK init and user authentication.
   *
   * @param userId - The backend user ID (for logging)
   */
  async sendAppsFlyerIdToBackend(): Promise<void> {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }

    try {
      const appsflyerId = await this.getAppsFlyerId();
      if (!appsflyerId) {
        console.warn('[AppsFlyer] No AppsFlyer ID available to send');
        return;
      }

      const platform = Capacitor.getPlatform(); // 'ios' or 'android'

      await apiClient.post('/users/appsflyer-id/', {
        appsflyerId,
        platform,
        // advertisingId is not easily accessible without native module
        // It will be sent as null/undefined
      });

      console.log('[AppsFlyer] Sent AppsFlyer ID to backend:', appsflyerId.slice(0, 8) + '...');
    } catch (error) {
      console.error('[AppsFlyer] Failed to send AppsFlyer ID to backend:', error);
    }
  },

  /**
   * Log an in-app event to AppsFlyer.
   *
   * @param eventName - The event name (e.g., 'af_complete_registration')
   * @param eventValue - Event parameters
   */
  async logEvent(eventName: string, eventValue: Record<string, unknown> = {}): Promise<void> {
    if (Capacitor.getPlatform() === 'web') {
      console.log('[AppsFlyer] Event (web, not sent):', eventName, eventValue);
      return;
    }

    try {
      const event: AFEvent = {
        eventName,
        eventValue,
      };

      await AppsFlyer.logEvent(event);
      console.log('[AppsFlyer] Event logged:', eventName);
    } catch (error) {
      console.error('[AppsFlyer] Failed to log event:', eventName, error);
    }
  },

  /**
   * Log a registration event.
   * Called when a user completes signup.
   *
   * @param method - Registration method (e.g., 'email', 'google', 'apple')
   */
  async logRegistration(method: string = 'email'): Promise<void> {
    await this.logEvent('af_complete_registration', {
      af_registration_method: method,
    });
  },

  /**
   * Log a login event.
   * Called when a user logs in.
   */
  async logLogin(): Promise<void> {
    await this.logEvent('af_login', {});
  },

  /**
   * Log a first restaurant rating event.
   * Key conversion event for the app.
   *
   * @param restaurantId - The restaurant ID
   * @param rating - The rating value
   */
  async logFirstRating(restaurantId: number, rating: number): Promise<void> {
    await this.logEvent('first_restaurant_rating', {
      restaurant_id: restaurantId.toString(),
      rating: rating.toString(),
    });
  },

  /**
   * Log a check-in event.
   *
   * @param restaurantId - The restaurant ID
   * @param restaurantName - The restaurant name
   */
  async logCheckIn(restaurantId: number, restaurantName: string): Promise<void> {
    await this.logEvent('af_checkin', {
      restaurant_id: restaurantId.toString(),
      restaurant_name: restaurantName,
    });
  },

  /**
   * Log a list creation event.
   *
   * @param listType - Type of list (e.g., 'personal', 'collaborative')
   * @param isPublic - Whether the list is public
   */
  async logListCreated(listType: string, isPublic: boolean): Promise<void> {
    await this.logEvent('list_created', {
      list_type: listType,
      is_public: isPublic.toString(),
    });
  },

  /**
   * Log a friend invite event.
   *
   * @param method - How the invite was sent (e.g., 'contacts', 'link', 'share')
   */
  async logInvite(method: string): Promise<void> {
    await this.logEvent('af_invite', {
      method,
    });
  },

  /**
   * Log a quiz completion event.
   *
   * @param quizType - Type of quiz completed
   */
  async logQuizCompleted(quizType: string): Promise<void> {
    await this.logEvent('quiz_completed', {
      quiz_type: quizType,
    });
  },

  /**
   * Check if the service is initialized.
   */
  get isInitialized(): boolean {
    return isInitialized;
  },
};

export default appsFlyerService;
